import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
  BarChart, Bar,
} from "recharts";
import { Mail, Eye, MousePointerClick, AlertOctagon, TrendingUp, ChevronRight } from "lucide-react";

const fmt = (n) => new Intl.NumberFormat("it-IT").format(n);
const pct = (n) => `${(n ?? 0).toFixed(1)}%`;

const StatTile = ({ label, value, sub, color, icon: Icon, testid }) => (
  <div className="card" data-testid={testid} style={{padding:16}}>
    <div className="flex items-center justify-between mb-2">
      <div className="stat-label">{label}</div>
      {Icon && <Icon size={16} color={color || "var(--mv-primary)"}/>}
    </div>
    <div className="stat-num" style={{color: color || "inherit"}}>{value}</div>
    {sub != null && <div className="text-small text-muted mt-1">{sub}</div>}
  </div>
);

export default function Engagement() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [days, setDays] = useState(90);
  const [loading, setLoading] = useState(true);
  const [listFilter, setListFilter] = useState("__ALL__");

  const load = async (d) => {
    setLoading(true);
    try {
      const res = await api.get(`/stats/engagement?days=${d}`);
      setData(res.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(days); }, [days]);

  // Recompute totals + daily timeline when a list filter is applied
  const view = useMemo(() => {
    if (!data) return null;
    if (listFilter === "__ALL__") return data;
    const row = data.per_list.find(l => l.listId === listFilter);
    if (!row) return { ...data, totals: { sent: 0, delivered: 0, opened: 0, clicked: 0, bounced: 0, open_rate: 0, click_rate: 0, bounce_rate: 0, ctr: 0 }, daily: [], per_list: [] };
    // For per-list view: synthesize totals from the row and best-effort daily reuse.
    // (Backend would aggregate filtered timeline; for now we show overall daily for context.)
    return {
      ...data,
      totals: {
        sent: row.sent, delivered: row.delivered, opened: row.opened,
        clicked: row.clicked, bounced: row.bounced, failed: row.failed,
        open_rate: row.open_rate, click_rate: row.click_rate,
        bounce_rate: row.bounce_rate, ctr: row.ctr,
      },
      per_list: [row],
    };
  }, [data, listFilter]);

  if (loading && !data) return <div data-testid="engagement-loading"><span className="spinner"/></div>;
  const t = view?.totals || {};
  const empty = (data?.totals?.sent || 0) === 0;

  return (
    <div data-testid="engagement-page">
      <div className="flex justify-between items-center mb-6" style={{flexWrap:"wrap",gap:12}}>
        <div>
          <div className="h-display">Engagement</div>
          <div className="text-muted mt-2">Open rate, click rate, bounce rate · finestra ultimi {days} giorni</div>
        </div>
        <div className="flex gap-2 items-center" style={{flexWrap:"wrap"}}>
          {data && data.per_list.length > 0 && (
            <select value={listFilter} onChange={(e)=>setListFilter(e.target.value)}
              data-testid="engagement-list-filter"
              className="select"
              style={{padding:"8px 10px",borderRadius:8,border:"1px solid #ddd",minWidth:200}}>
              <option value="__ALL__">Tutte le liste ({data.per_list.length})</option>
              {data.per_list.map(l => (
                <option key={l.listId} value={l.listId}>{l.listName}</option>
              ))}
            </select>
          )}
          <div className="flex gap-2" data-testid="engagement-window-switch">
            {[7, 30, 90].map(d => (
              <button key={d} onClick={() => setDays(d)} data-testid={`window-${d}`}
                className={`btn btn-sm ${days === d ? "btn-primary" : "btn-secondary"}`}>
                {d} giorni
              </button>
            ))}
          </div>
        </div>
      </div>

      {empty ? (
        <div className="card text-center" style={{padding:48}} data-testid="engagement-empty">
          <TrendingUp size={48} color="var(--mv-text-muted)" style={{margin:"0 auto 16px"}}/>
          <div className="h-section" style={{marginBottom:8}}>Nessun dato di engagement ancora</div>
          <div className="text-muted">
            Configura il webhook Resend (dashboard Resend → Webhooks → URL: <code>{window.location.origin}/api/webhooks/resend</code>) e invia la prima campagna per vedere open/click qui.
          </div>
        </div>
      ) : (
      <>
      <div className="grid gap-3 mb-6" style={{gridTemplateColumns:"repeat(auto-fit, minmax(170px,1fr))"}}>
        <StatTile label="Email inviate" value={fmt(t.sent)} icon={Mail} testid="tile-sent" />
        <StatTile label="Consegnate" value={fmt(t.delivered)} sub={`${pct((t.delivered/(t.sent||1))*100)} su inviate`} color="var(--mv-success)" testid="tile-delivered"/>
        <StatTile label="Aperte" value={fmt(t.opened)} sub={`Open rate ${pct(t.open_rate)}`} color="var(--mv-primary)" icon={Eye} testid="tile-opened"/>
        <StatTile label="Cliccate" value={fmt(t.clicked)} sub={`Click rate ${pct(t.click_rate)} · CTR ${pct(t.ctr)}`} color="var(--mv-success)" icon={MousePointerClick} testid="tile-clicked"/>
        <StatTile label="Bounce" value={fmt(t.bounced)} sub={`Bounce rate ${pct(t.bounce_rate)}`} color={t.bounce_rate > 5 ? "var(--mv-danger)" : "var(--mv-text-muted)"} icon={AlertOctagon} testid="tile-bounced"/>
      </div>

      <div className="card mb-6" style={{padding:18}} data-testid="engagement-timeline">
        <div className="flex justify-between items-center mb-4">
          <div className="h-section">Timeline giornaliera</div>
          <div className="text-small text-muted">Inviate · Aperte · Cliccate</div>
        </div>
        <ResponsiveContainer width="100%" height={280}>
          <AreaChart data={view.daily} margin={{top:5,right:8,bottom:5,left:0}}>
            <defs>
              <linearGradient id="gSent" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#c95a1a" stopOpacity={0.4}/>
                <stop offset="100%" stopColor="#c95a1a" stopOpacity={0}/>
              </linearGradient>
              <linearGradient id="gOpened" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#16a34a" stopOpacity={0.4}/>
                <stop offset="100%" stopColor="#16a34a" stopOpacity={0}/>
              </linearGradient>
              <linearGradient id="gClicked" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#2563eb" stopOpacity={0.4}/>
                <stop offset="100%" stopColor="#2563eb" stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#eee"/>
            <XAxis dataKey="date" tick={{fontSize:11}}/>
            <YAxis tick={{fontSize:11}} allowDecimals={false}/>
            <Tooltip/>
            <Legend wrapperStyle={{fontSize:12}}/>
            <Area type="monotone" dataKey="sent" stroke="#c95a1a" fill="url(#gSent)" name="Inviate"/>
            <Area type="monotone" dataKey="opened" stroke="#16a34a" fill="url(#gOpened)" name="Aperte"/>
            <Area type="monotone" dataKey="clicked" stroke="#2563eb" fill="url(#gClicked)" name="Cliccate"/>
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="card" style={{padding:0}} data-testid="engagement-per-list">
        <div className="flex justify-between items-center" style={{padding:18,borderBottom:"1px solid #eee"}}>
          <div className="h-section">Performance per lista</div>
          <div className="text-small text-muted">{view.per_list.length} liste {listFilter !== "__ALL__" ? "(filtrata)" : "con email inviate"}</div>
        </div>
        {view.per_list.length === 0 ? (
          <div className="text-muted text-center" style={{padding:40}}>Nessuna lista con email nei ultimi {days} giorni.</div>
        ) : (
          <div style={{overflowX:"auto"}}>
            <table className="w-full" style={{borderCollapse:"collapse"}}>
              <thead>
                <tr style={{background:"#fafafa",fontSize:12,textTransform:"uppercase",color:"var(--mv-text-muted)"}}>
                  <th style={{textAlign:"left",padding:"10px 14px"}}>Lista</th>
                  <th style={{textAlign:"right",padding:"10px 8px"}}>Inviate</th>
                  <th style={{textAlign:"right",padding:"10px 8px"}}>Aperte</th>
                  <th style={{textAlign:"right",padding:"10px 8px"}}>Open rate</th>
                  <th style={{textAlign:"right",padding:"10px 8px"}}>Clic</th>
                  <th style={{textAlign:"right",padding:"10px 8px"}}>Click rate</th>
                  <th style={{textAlign:"right",padding:"10px 8px"}}>CTR</th>
                  <th style={{textAlign:"right",padding:"10px 14px"}}>Bounce</th>
                  <th style={{width:30,padding:"10px 8px"}}></th>
                </tr>
              </thead>
              <tbody>
                {view.per_list.map(l => (
                  <tr key={l.listId} data-testid={`list-row-${l.listId}`}
                    onClick={()=>navigate(`/liste/${l.listId}`)}
                    style={{borderTop:"1px solid #f0f0f0",cursor:"pointer"}}
                    onMouseEnter={(e)=>e.currentTarget.style.background="#fafafa"}
                    onMouseLeave={(e)=>e.currentTarget.style.background=""}>
                    <td style={{padding:"12px 14px",fontWeight:600}}>{l.listName}</td>
                    <td style={{padding:"12px 8px",textAlign:"right"}}>{fmt(l.sent)}</td>
                    <td style={{padding:"12px 8px",textAlign:"right"}}>{fmt(l.opened)}</td>
                    <td style={{padding:"12px 8px",textAlign:"right",fontWeight:600,color:"var(--mv-primary)"}}>{pct(l.open_rate)}</td>
                    <td style={{padding:"12px 8px",textAlign:"right"}}>{fmt(l.clicked)}</td>
                    <td style={{padding:"12px 8px",textAlign:"right",fontWeight:600,color:"var(--mv-success)"}}>{pct(l.click_rate)}</td>
                    <td style={{padding:"12px 8px",textAlign:"right"}}>{pct(l.ctr)}</td>
                    <td style={{padding:"12px 14px",textAlign:"right",color: l.bounce_rate > 5 ? "var(--mv-danger)" : "var(--mv-text-muted)"}}>
                      {fmt(l.bounced)} ({pct(l.bounce_rate)})
                    </td>
                    <td style={{padding:"12px 8px",color:"var(--mv-text-muted)"}}>
                      <ChevronRight size={16}/>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {view.per_list.length > 1 && listFilter === "__ALL__" && (
        <div className="card mt-6" style={{padding:18}} data-testid="engagement-compare-chart">
          <div className="h-section mb-4">Confronto Open vs Click per lista</div>
          <ResponsiveContainer width="100%" height={Math.max(240, view.per_list.length * 38)}>
            <BarChart data={view.per_list} layout="vertical" margin={{top:5,right:8,bottom:5,left:8}}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee"/>
              <XAxis type="number" unit="%" tick={{fontSize:11}}/>
              <YAxis type="category" dataKey="listName" tick={{fontSize:11}} width={160}/>
              <Tooltip formatter={(v) => `${v}%`}/>
              <Legend wrapperStyle={{fontSize:12}}/>
              <Bar dataKey="open_rate" fill="#c95a1a" name="Open rate %" radius={[0,4,4,0]}
                onClick={(d)=>d?.listId && navigate(`/liste/${d.listId}`)}
                style={{cursor:"pointer"}}/>
              <Bar dataKey="click_rate" fill="#16a34a" name="Click rate %" radius={[0,4,4,0]}
                onClick={(d)=>d?.listId && navigate(`/liste/${d.listId}`)}
                style={{cursor:"pointer"}}/>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
      </>
      )}
    </div>
  );
}
